<template>
  <div class="salesman-management">
    <div class="page-card">
      <div class="search-section">
        <el-form
          :model="searchForm"
          class="search-form"
          label-position="left"
          label-width="84px"
        >
          <div class="search-row">
            <el-form-item
              :label="$t('workbench.salesmanName')"
              class="search-field"
            >
              <el-input
                v-model="searchForm.userRealName"
                :placeholder="$t('workbench.placeholderSalesmanName')"
                clearable
                class="search-input"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            <el-form-item
              :label="$t('workbench.phone')"
              class="search-field"
            >
              <el-input
                v-model="searchForm.phone"
                :placeholder="$t('workbench.placeholderPhone')"
                clearable
                class="search-input"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
            <div class="search-actions">
              <el-button
                type="primary"
                size="default"
                class="btn-query"
                @click="handleSearch"
              >
                <el-icon><Search /></el-icon>
                {{ $t('common.search') }}
              </el-button>
              <el-button
                size="default"
                class="btn-reset"
                @click="handleReset"
              >
                <el-icon><Refresh /></el-icon>
                {{ $t('common.reset') }}
              </el-button>
            </div>
          </div>
        </el-form>
      </div>

      <div class="action-bar">
        <el-button
          type="primary"
          @click="handleAdd"
        >
          {{ $t('workbench.addSalesman') }}
        </el-button>
      </div>

      <div class="table-wrap">
        <el-table
          v-loading="loading"
          :data="salesmanList"
          style="width: 100%"
          stripe
          border
          class="salesman-table"
          height="560"
        >
        <template #empty>
          <EmptyPlaceholder v-if="!loading" />
        </template>
        <el-table-column
          type="index"
          :label="$t('workbench.serialNumber')"
          width="60"
          align="center"
        />
        <el-table-column
          prop="userRealName"
          :label="$t('workbench.salesmanName')"
          align="center"
          min-width="120"
        />
        <el-table-column
          prop="idCardNo"
          :label="$t('workbench.idCard')"
          align="center"
          min-width="120"
        />
        <el-table-column
          prop="phone"
          :label="$t('workbench.phone')"
          align="center"
          min-width="130"
        />
        <el-table-column
          prop="status"
          :label="$t('common.status')"
          align="center"
          width="120"
        >
          <template #default="{ row }">
            <el-switch
              :model-value="row.status === '0'"
              active-color="#13ce66"
              inactive-color="#ff4949"
              @change="handleStatusChange(row)"
            />
          </template>
        </el-table-column>
        <el-table-column
          prop="createTime"
          :label="$t('workbench.inviteTime')"
          align="center"
          min-width="158"
        />
        <el-table-column
          :label="$t('common.operation')"
          align="center"
          width="248"
          fixed="right"
        >
          <template #default="{ row }">
            <el-button
              type="primary"
              plain
              size="small"
              @click="handleOpenAssign(row)"
            >
              分配客户
            </el-button>
            <el-button
              type="danger"
              plain
              size="small"
              @click="handleDelete(row)"
            >
              {{ $t('common.remove') }}
            </el-button>
          </template>
        </el-table-column>
        </el-table>
      </div>

      <div class="pagination-section">
        <el-pagination
          v-model:current-page="pagination.pageNum"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <InvitePersonalDialog
      v-model="addDialogVisible"
      :title="$t('workbench.addSalesman')"
      :confirm-button-text="$t('inviteDialog.confirmInvite')"
      :require-verified="true"
      @confirm="handleConfirmAdd"
    />

    <AssignCustomerDialog
      v-model="assignDialogVisible"
      :salesman="currentSalesman"
      @success="loadSalesmanList"
    />
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { Search, Refresh } from '@element-plus/icons-vue'
import { getSalesmanList, removeSalesmanById, editSalesmanStatus } from '@/api/workbench'
import { sendApproval } from '@/api/approval'
import InvitePersonalDialog from '@/components/InviteDialog/InvitePersonalDialog.vue'
import AssignCustomerDialog from './AssignCustomerDialog.vue'
import EmptyPlaceholder from '@/components/EmptyPlaceholder.vue'
import { formatMessageContent, showEnhancedConfirmDialog } from '@/utils/messageUtils'
import { BusinessTypeEnum } from '@/enums/BusinessTypeEnum'

export default {
  name: 'SalesmanManagement',
  components: {
    InvitePersonalDialog,
    AssignCustomerDialog,
    EmptyPlaceholder,
    Search,
    Refresh,
  },
  data() {
    return {
      loading: false,
      searchForm: {
        userRealName: '',
        phone: ''
      },
      salesmanList: [],
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      // 新增业务员弹窗相关
      addDialogVisible: false,
      // 分配客户弹窗相关（仅保留打开弹窗所需状态，弹窗内容由 AssignCustomerDialog 负责）
      assignDialogVisible: false,
      currentSalesman: { userId: null, userRealName: '' }
    }
  },
  mounted() {
    this.loadSalesmanList()
  },
  methods: {
    /**
     * 加载业务员列表
     */
    async loadSalesmanList() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        }
        
        // 添加搜索条件（如果有值）
        if (this.searchForm.userRealName) {
          params.userRealName = this.searchForm.userRealName
        }
        if (this.searchForm.phone) {
          params.userPhone = this.searchForm.phone
        }
        
        const response = await getSalesmanList(params)
        
        if (response && response.code === 200) {
          // 处理返回的数据
          this.salesmanList = (response.rows || []).map(item => ({
            ...item,
            userRealName: item.userRealName || '',
            phone: item.userPhone || '',
            createTime: item.createTime || ''
          })) 
          this.pagination.total = response.total || 0
        }
      } catch (error) {
        console.error('加载业务员列表失败:', error)
        ElMessage.error(this.$t('workbench.loadListFail'))
        this.salesmanList = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    /**
     * 搜索
     */
    handleSearch() {
      this.pagination.pageNum = 1
      this.loadSalesmanList()
    },
    /**
     * 重置搜索条件
     */
    handleReset() {
      this.searchForm = {
        userRealName: '',
        phone: ''
      }
      this.handleSearch()
    },
    /**
     * 打开新增业务员弹窗
     */
    handleAdd() {
      this.addDialogVisible = true
    },
    /**
     * 确认添加业务员
     */
    async handleConfirmAdd(data) {
      const { queryResult } = data

      if (!queryResult || !queryResult.userId) {
        ElMessage.warning(this.$t('inviteDialog.pleaseQueryFirst'))
        return
      }

      try {
        // 获取当前企业信息
        // const enterpriseResponse = await getCurrentEnterprise()
        // const enterpriseData = enterpriseResponse.data || enterpriseResponse
        // const entId = enterpriseData.id
        // 从缓存里获取当前企业ID
        const entId = this.$auth.getCurrentMainBodyId()
        
        if (!entId) {
          ElMessage.error(this.$t('workbench.cannotGetEnterpriseInfo'))
          return
        }

        // 调用通用审批发送接口
        const approvalData = {
          applicantId: entId, // 申请人ID（当前企业主体ID）
          approverId: queryResult.userId, // 审批人ID（被邀请用户的主体ID）
          businessType: BusinessTypeEnum.ORGANIZATION_INVITE_SALESMAN.code, // 业务类型：机构业务员邀请
          remark: this.$t('workbench.inviteSalesmanRemark', { name: queryResult.realName }) // 备注
        }
        
        await sendApproval(approvalData)
        
        ElMessage.success(this.$t('workbench.inviteSuccess'))
        
        // 刷新业务员列表
        await this.loadSalesmanList()
        // 关闭弹窗
        this.addDialogVisible = false
      } catch (error) {
        console.error('发送审批申请失败:', error)
        ElMessage.error(error.message || this.$t('workbench.inviteFail'))
      }
    },
    /**
     * 状态切换
     */
    async handleStatusChange(row) {
      const oldStatus = row.status
      const newStatus = oldStatus === '0' ? '1' : '0'
      const isEnable = newStatus === '0'
      const statusColor = isEnable ? '#13ce66' : '#ff4949'
      const statusText = isEnable ? this.$t('workbench.enable') : this.$t('workbench.disable')
      const statusTextWithColor = `<span color="${statusColor}">${statusText}</span>`

      const message = formatMessageContent(
        this.$t('workbench.confirmStatusChange', { name: row.userRealName, status: statusTextWithColor })
      )
      const confirmed = await showEnhancedConfirmDialog(
        message,
        this.$t('common.tips'),
        this.$t('common.confirm'),
        this.$t('common.cancel'),
        { type: 'warning', dangerouslyUseHTMLString: true }
      )
      if (!confirmed) {
        row.status = oldStatus
        return
      }

      try {
        const response = await editSalesmanStatus({
          id: row.id,
          status: newStatus
        })

        if (response && response.code === 200) {
          ElMessage.success(this.$t('workbench.statusChangeSuccess'))
          row.status = newStatus
        } else {
          row.status = oldStatus
        }
      } catch (error) {
        console.error('状态切换失败:', error)
        ElMessage.error(error.message || this.$t('workbench.statusChangeFail'))
        row.status = oldStatus
      }
    },
    /**
     * 删除业务员
     */
    async handleDelete(row) {
      try {
        const confirmed = await showEnhancedConfirmDialog(
          this.$t('workbench.confirmRemoveSalesman', { name: row.userRealName }),
          this.$t('common.tips'),
          this.$t('common.confirm'),
          this.$t('common.cancel'),
          { type: 'warning' }
        )
        if (!confirmed) return

        // 调用API删除业务员
        const response = await removeSalesmanById(row.id)
        if (response && response.code === 200) {
          ElMessage.success(this.$t('workbench.removeSuccess'))
          this.loadSalesmanList()
        }
      } catch (error) {
        // 用户取消删除或删除失败
        if (error !== 'cancel') {
          console.error('删除业务员失败:', error)
          ElMessage.error(error.message || this.$t('workbench.removeFail'))
        }
      }
    },
    /**
     * 分页大小改变
     */
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.pageNum = 1
      this.loadSalesmanList()
    },
    /**
     * 页码改变
     */
    handlePageChange(val) {
      this.pagination.pageNum = val
      this.loadSalesmanList()
    },
    /** 打开分配客户弹窗 */
    handleOpenAssign(row) {
      if (!row.userId) {
        ElMessage.warning('业务员信息不完整，无法分配客户')
        return
      }
      this.currentSalesman = { userId: row.userId, userRealName: row.userRealName || row.phone || '' }
      this.assignDialogVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
.salesman-management {
  min-height: 100%;
  box-sizing: border-box;

  .page-card {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    padding: 0 0 40px;
  }

  .search-section {
    margin-bottom: 16px;
    padding: 0;
    box-sizing: border-box;
  }

  .search-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .search-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px 68px;
  }

  :deep(.search-field.el-form-item) {
    margin-bottom: 0;
    margin-right: 0;
    display: inline-flex;
    align-items: center;
  }

  :deep(.search-field .el-form-item__label) {
    width: 84px;
    padding-right: 12px;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: 500;
    color: #191919;
    text-align: left;
    justify-content: flex-start;
  }

  :deep(.search-field .el-form-item__content) {
    margin-left: 0 !important;
  }

  .search-input {
    width: 280px;
  }

  .search-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-left: auto;
  }

  .btn-query {
    padding: 0 12px;
    border-radius: 4px;
    font-size: 13px;
    background: #0d62f3 !important;
    border-color: #0d62f3 !important;

    &:hover {
      background: #0b56e0 !important;
      border-color: #0b56e0 !important;
    }

    .el-icon {
      margin-right: 4px;
      font-size: 14px;
    }
  }

  .btn-reset {
    padding: 0 12px;
    border-radius: 4px;
    font-size: 13px;
    color: #191919;
    background: #ffffff !important;
    border: 1px solid #e8e8e8 !important;

    &:hover {
      border-color: #c9cdd4 !important;
      background: #f7f8fa !important;
      color: #191919 !important;
    }

    .el-icon {
      margin-right: 4px;
      font-size: 14px;
      color: #0d62f3;
    }
  }

  .action-bar {
    padding: 0 0 20px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  .salesman-table {
    border-radius: 8px;
    border: 1px solid #ebeef5;
    overflow: hidden;

    :deep(th.el-table__cell) {
      background-color: #f5f7fa !important;
      color: #333;
      font-weight: 600;
      padding: 12px 0;
    }

    :deep(td.el-table__cell) {
      padding: 12px 0;
    }

    .el-button {
      border-radius: 4px;
    }
  }

  .pagination-section {
    padding-top: 26px;
    padding-bottom: 4px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}

@media (max-width: 768px) {
  .salesman-management {
    :deep(.search-field.el-form-item) {
      width: 100%;
      min-width: 0;
      flex-direction: column;
      align-items: stretch;
    }

    :deep(.search-field .el-form-item__label) {
      width: 100% !important;
      padding-right: 0;
      padding-bottom: 6px;
      line-height: 1.4;
    }

    :deep(.search-field .el-form-item__content) {
      width: 100%;
    }

    .search-input {
      width: 100%;
      min-width: 0;
    }

    .search-actions {
      width: 100%;
      margin-left: 0;

      .el-button {
        flex: 1;
        min-width: 0;
      }
    }

    .action-bar {
      .el-button {
        width: 100%;
      }
    }

    .table-wrap {
      width: 100%;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .salesman-table {
      min-width: 860px;
      height: 420px !important;
    }

    .pagination-section {
      justify-content: center;

      :deep(.el-pagination) {
        width: 100%;
        flex-wrap: wrap;
        justify-content: center;
        row-gap: 8px;
      }

      :deep(.el-pagination__sizes),
      :deep(.el-pagination__jump) {
        display: none;
      }
    }
  }
}

@media (max-width: 480px) {
  .salesman-management {
    .salesman-table {
      min-width: 720px;
      height: 360px !important;

      :deep(.el-button) {
        padding: 4px 8px;
        font-size: 12px;
      }
    }

    .pagination-section {
      :deep(.el-pagination__total) {
        display: none;
      }
    }
  }
}
</style>

